import { useEffect, useId, useState } from 'react';
import { API_URL, apiFetch } from '../lib/api-client';
import type { CommunicationMessage } from './communications-model';

type Media = NonNullable<CommunicationMessage['media']>[number];
type Job = { id: string; jobNumber: string | null; description: string | null; customerName: string | null };
export function SmsImageAttachment({ media, canAttachPhotos = false }: { media: Media; canAttachPhotos?: boolean }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobId, setJobId] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const controlId = useId();
  const supported = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(media.contentType ?? '');
  const extension = ({ 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' } as Record<string, string>)[media.contentType ?? ''] ?? 'image';
  const filename = `text-image-${(media.id ?? 'attachment').replace(/[^a-zA-Z0-9_-]/g, '')}.${extension}`;
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true); setError(''); setJobId('');
    const timer = window.setTimeout(() => {
      void apiFetch(`/api/v1/workspace/dispatch/jobs?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal })
        .then(async response => {
          if (!response.ok) throw new Error('Jobs could not be loaded. Try searching again.');
          const payload = await response.json() as { data: { items: Job[] } };
          if (!controller.signal.aborted) setJobs(payload.data.items);
        }).catch(cause => { if (!controller.signal.aborted) setError(cause.message); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [open, query]);
  async function image() {
    const response = await apiFetch(media.contentUrl!);
    if (!response.ok) throw new Error('This image could not be downloaded. Please try again.');
    return response.blob();
  }
  async function download() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const url = URL.createObjectURL(await image());
      const link = document.createElement('a');
      link.href = url; link.download = filename; document.body.append(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Image download failed.'); }
    finally { setBusy(false); }
  }
  async function attach() {
    if (busy || loading || !jobId || !canAttachPhotos || !supported) return;
    setBusy(true); setError(''); setSaved('');
    try {
      let photo = await image();
      let photoFilename = filename;
      if (media.contentType === 'image/gif') {
        const bitmap = await createImageBitmap(photo);
        try {
          const canvas = document.createElement('canvas'); canvas.width = bitmap.width; canvas.height = bitmap.height;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('This image could not be converted for the job.');
          context.drawImage(bitmap, 0, 0);
          photo = await new Promise<Blob>((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('This image could not be converted for the job.')), 'image/png'));
          photoFilename = filename.replace(/\.gif$/, '.png');
        } finally { bitmap.close(); }
      }
      const form = new FormData(); form.append('photo', photo, photoFilename);
      const response = await apiFetch(`/api/v1/workspace/jobs/${encodeURIComponent(jobId)}/photos`, { method: 'POST', body: form });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error?.message || 'The image could not be added to this job. Please try again.');
      }
      const job = jobs.find(item => item.id === jobId);
      setSaved(`Image added to job ${job?.jobNumber || jobId}.`); setOpen(false);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'The image could not be added to the job.'); }
    finally { setBusy(false); }
  }
  return <div className="comms-image-attachment">
    <a href={`${API_URL}${media.contentUrl}`} target="_blank" rel="noreferrer"><img className="max-h-48 rounded border border-[var(--line)]" src={`${API_URL}${media.contentUrl}`} alt="SMS image attachment" /></a>
    <div className="comms-image-actions"><button type="button" className="comms-link" disabled={busy} onClick={() => void download()}>Download image</button>
      {canAttachPhotos && <button type="button" className="comms-link" disabled={busy || !supported} onClick={() => { setOpen(value => !value); setSaved(''); }}>Add to job</button>}
    </div>
    {canAttachPhotos && media.contentType === 'image/gif' && <p className="comms-muted">GIFs are saved to jobs as a still PNG image. Downloads keep the original GIF.</p>}
    {canAttachPhotos && !supported && <p className="comms-muted">Job photos support JPEG, PNG, and WebP. Download this image to convert it first.</p>}
    {open && <form className="comms-image-job" onSubmit={event => { event.preventDefault(); void attach(); }}>
      <label htmlFor={`${controlId}-search`}>Find an existing job</label>
      <input id={`${controlId}-search`} type="search" placeholder="Customer name or job number" value={query} disabled={busy} onChange={event => setQuery(event.target.value)} />
      <label htmlFor={`${controlId}-job`}>Job</label>
      <select id={`${controlId}-job`} value={jobId} disabled={busy || loading} onChange={event => setJobId(event.target.value)}>
        <option value="">{loading ? 'Loading jobs…' : 'Choose a job'}</option>
        {!loading && jobs.map(job => <option key={job.id} value={job.id}>{[job.jobNumber, job.customerName, job.description].filter(Boolean).join(' · ')}</option>)}
      </select>
      {!loading && !error && !jobs.length && <p className="comms-muted">No matching jobs. Try another search.</p>}
      <div className="comms-image-actions"><button type="submit" className="comms-button" disabled={busy || loading || !jobId}>{busy ? 'Adding image…' : 'Save image to job'}</button><button type="button" className="comms-link" disabled={busy} onClick={() => setOpen(false)}>Cancel</button></div>
    </form>}
    {error && <p role="alert" className="comms-review-error">{error}</p>}
    {saved && <p role="status" className="comms-muted">{saved}</p>}
  </div>;
}
